/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Membership } from '../../@types/membership';
import type { RoomMemberEventHandlerMap } from '../../@types/room-member';
import type { XHubChatEvent } from '../event/event';
import type { User } from '../user/user';
import { EventType } from '../../@types/event';
import { KnownMembership } from '../../@types/membership';
import { RoomMemberEvent } from '../../@types/room-member';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { removeDirectionOverrideChars, removeHiddenChars } from '../../utils';
import { logger } from '../../utils/logger';

export class RoomMember extends TypedEventEmitter<RoomMemberEvent, RoomMemberEventHandlerMap> {
  private _isOutOfBand = false;
  private modified = -1;
  public requestedProfileInfo = false; // used by sync.ts

  // XXX these should be read-only
  /**
   * True if the room member is currently typing.
   */
  public typing = false;
  /**
   * The human-readable name for this room member. This will be
   * disambiguated with a suffix of " (\@user_id:matrix.org)" if another member shares the
   * same displayname.
   */
  public name: string;
  /**
   * The ambiguous displayname of this room member.
   */
  public rawDisplayName: string;

  /**
   * The User object for this room member, if one exists.
   */
  public user?: User;
  /**
   * The membership state for this room member e.g. 'join'.
   */
  public membership?: Membership;
  /**
   * The events describing this RoomMember.
   */
  public events: {
    /**
     * The m.room.member event for this RoomMember.
     */
    member?: XHubChatEvent;
  } = {};

  /**
   * An optional description for this room member.
   */
  public description?: string;

  /**
   * Construct a new room member.
   * @param roomId - The room ID of the member.
   * @param userId - The user ID of the member.
   * @param channel_slug - The channel slug of this room member.
   */
  public constructor(public readonly roomId: string, public readonly userId: string, public channel_slug: string) {
    super();

    this.name = userId;
    this.rawDisplayName = userId;
    this.updateModifiedTime();
  }

  /**
   * Mark the member as coming from a channel that is not sync
   */
  public markOutOfBand(): void {
    this._isOutOfBand = true;
  }

  /**
   * @returns does the member come from a channel that is not sync?
   * This is used to store the member separately
   * from the sync state so it available across browser sessions.
   */
  public isOutOfBand(): boolean {
    return this._isOutOfBand;
  }

  /**
   * Set a description for this room member.
   * @param description - The description to set
   */
  public setDescription(description: string): void {
    this.description = description;
  }

  /**
   * Update this room member's membership event. May fire "RoomMember.name" if
   * this event updates this member's name.
   * @param event - The `m.room.member` event
   * when calculating (e.g. for disambiguating users with the same name).
   *
   * @remarks
   * Fires {@link RoomMemberEvent.Name}
   * Fires {@link RoomMemberEvent.Membership}
   */
  public setMembershipEvent(event: XHubChatEvent): void {
    if (event.getType() !== EventType.RoomMember) return;

    this._isOutOfBand = false;
    this.events.member = event;

    const oldMembership = this.membership;
    this.membership = event.getDirectionalContent().membership;
    if (this.membership === undefined) {
      // (logs event content, although only of membership events)
      logger.trace(
        `membership event with membership undefined (forwardLooking: ${event.forwardLooking})!`,
        event.getContent(),
        `prev content is `,
        event.getPrevContent(),
      );
    }

    // not quite raw: we strip direction override chars so it can safely be inserted into
    // blocks of text without breaking the text direction
    this.rawDisplayName = removeDirectionOverrideChars(event.getDirectionalContent().displayname ?? '');
    if (!this.rawDisplayName || !removeHiddenChars(this.rawDisplayName)) {
      this.rawDisplayName = this.userId;
    }

    // If have change the name, then we fire the name event
    if (oldMembership !== this.membership) {
      this.updateModifiedTime();
      this.emit(RoomMemberEvent.Membership, event, this, oldMembership);
    }
  }

  /**
   * Update this room member's typing event. May fire "RoomMember.typing" if
   * this event changes this member's typing state.
   * @param event - The typing event
   *
   * @remarks
   * Fires {@link RoomMemberEvent.Typing}
   */
  public setTypingEvent(event: XHubChatEvent): void {
    if (event.getType() !== 'm.typing') {
      return;
    }
    const oldTyping = this.typing;
    this.typing = false;
    const typingList = event.getContent().user_ids;
    if (!Array.isArray(typingList)) {
      // malformed event :/ bail early. TODO: whine?
      return;
    }
    if (typingList.includes(this.userId)) {
      this.typing = true;
    }
    if (oldTyping !== this.typing) {
      this.updateModifiedTime();
      this.emit(RoomMemberEvent.Typing, event, this);
    }
  }

  /**
   * Update the last modified time to the current time.
   */
  private updateModifiedTime(): void {
    this.modified = Date.now();
  }

  /**
   * Get the timestamp when this RoomMember was last updated. This timestamp is
   * updated when properties on this RoomMember are updated.
   * It is updated <i>before</i> firing events.
   * @returns The timestamp
   */
  public getLastModifiedTime(): number {
    return this.modified;
  }

  public isKicked(): boolean {
    return (
      this.membership === KnownMembership.Leave
      && this.events.member !== undefined
      && this.events.member.getSender() !== this.events.member.getStateKey()
    );
  }

  /**
   * get the avatar url, either from a state event, or from a lazily loaded member
   * @returns the avatar url
   */
  public getAvatarUrl(): string | undefined {
    if (this.events.member) {
      return this.events.member.getDirectionalContent().avatar_url;
    } else if (this.user) {
      return this.user.avatarUrl;
    }
  }
}
